/**
 * Calcolo "Prossima scadenza SLA" per prodotto (colonna lista prodotti).
 *
 * Per ogni prodotto con almeno un report SRP "open" (thread non risolto/non necessario)
 * si calcola il tempo rimanente prima della prossima scadenza CRA Art.14:
 *   - early warning NON ancora inviato → fase 'early', 24h dall'apertura.
 *   - early warning già inviato, thread ancora open → fase 'vulnerability', 72h dall'apertura.
 *   - notifica 72h già registrata → nessuna scadenza 24h/72h in corso (timer fermi): report saltato.
 * Con più release (report) aperte per lo stesso prodotto si picka il timer minimo
 * (il remainingHours più piccolo = la scadenza più urgente).
 *
 * Logica pura (nessun I/O, clock iniettabile): testabile isolate e deterministica.
 *
 * Linkaggio report → prodotto: il subjectRef dei draft auto-creati è `release:<releaseProjectUuid>`;
 * la mappa `releaseToProduct` (costruita dai bundle attivi) risolve l'UUID della release nel
 * productInternalId. I report con subjectRef non riconducibile a una release o la cui release
 * non è in mappa sono saltati (degradazione graziosa).
 */

import type { SlaTimer, SrpReport } from '../srp/types.js';
import { slaAnchorMs, timerForReport } from '../srp/slaAnchor.js';

/** SLA per prodotto: ore rimanenti alla prossima scadenza + fase corrente. */
export interface ProductNextSla {
  /** Ore rimanenti (può essere negativo = scaduto/breached). */
  remainingHours: number;
  /** Fase CRA Art.14: 'early' (24h) o 'vulnerability' (72h). */
  phase: 'early' | 'vulnerability';
}

/** Soglie SLA in ore (CRA Art.14(2)). */
const EARLY_DEADLINE_HOURS = 24;
const VULNERABILITY_DEADLINE_HOURS = 72;

/** Prefisso subjectRef dei draft SRP per release. */
const RELEASE_SUBJECT_PREFIX = 'release:';

/**
 * Determina se il thread di un report SRP è "open" (non risolto/non necessario).
 * threadStatus undefined → default semantico 'open' (il DB default è 'open').
 */
function isThreadOpen(report: SrpReport): boolean {
  const thread = report.threadStatus ?? 'open';
  return thread !== 'resolved' && thread !== 'not_needed';
}

/**
 * True se il primo step di early warning (24h) è già stato inviato per il report.
 * Discriminatore: payload.checklist.earlyWarning true OPPURE una notifica
 * con step='early_warning' in payload.notifications.
 */
function earlyWarningSent(report: SrpReport): boolean {
  if (report.payload.checklist?.earlyWarning === true) return true;
  return (report.payload.notifications ?? []).some((n) => n.step === 'early_warning');
}

/**
 * Estrae il releaseProjectUuid dal subjectRef (`release:<uuid>`), o null se il formato
 * non corrisponde (es. report manuali con subjectRef = CVE id).
 */
function releaseUuidFromSubject(report: SrpReport): string | null {
  const { subjectRef } = report;
  if (!subjectRef.startsWith(RELEASE_SUBJECT_PREFIX)) return null;
  const uuid = subjectRef.slice(RELEASE_SUBJECT_PREFIX.length);
  return uuid.length > 0 ? uuid : null;
}

/**
 * Calcola, per ogni prodotto, la prossima scadenza SLA a partire dai report SRP aperti.
 *
 * @param reports           - Tutti i report SRP (GET /srp/reports).
 * @param releaseToProduct  - Mappa releaseProjectUuid → productInternalId (da GET /bundles).
 * @param now               - Istante corrente (ms) per il calcolo del tempo trascorso.
 * @param timers            - Timer SLA (GET /sla/timers): ancora del clock per report; se
 *                            assenti si ricade su detectedAt/createdAt del report.
 * @returns Map productInternalId → { remainingHours, phase } (min tra le release aperte).
 */
export function computeProductNextSla(
  reports: SrpReport[],
  releaseToProduct: Map<string, string>,
  now: number,
  timers?: SlaTimer[],
): Map<string, ProductNextSla> {
  const result = new Map<string, ProductNextSla>();

  for (const report of reports) {
    if (!isThreadOpen(report)) continue;

    const releaseUuid = releaseUuidFromSubject(report);
    if (!releaseUuid) continue;
    const productInternalId = releaseToProduct.get(releaseUuid);
    if (!productInternalId) continue;

    // Punto zero SLA: timer del report, poi detectedAt, poi creazione del thread.
    const opening = slaAnchorMs(report, timerForReport(report, timers));
    if (opening === null) continue;

    // Notifica 72h registrata: i timer 24h/72h sono fermi, nessuna scadenza da mostrare qui.
    if ((report.payload.notifications ?? []).some((n) => n.step === 'vulnerability')) continue;

    const elapsedHours = (now - opening) / 3_600_000;
    const phase: ProductNextSla['phase'] = earlyWarningSent(report) ? 'vulnerability' : 'early';
    const deadlineHours =
      phase === 'vulnerability' ? VULNERABILITY_DEADLINE_HOURS : EARLY_DEADLINE_HOURS;
    const remainingHours = deadlineHours - elapsedHours;

    const existing = result.get(productInternalId);
    if (!existing || remainingHours < existing.remainingHours) {
      result.set(productInternalId, { remainingHours, phase });
    }
  }

  return result;
}

/**
 * Formatta le ore rimanenti come stringa compatta ("20h", "9h").
 * Se remainingHours <= 0 → "scaduto" (coerente con formatRemainingMs di SlaCountdown).
 */
export function formatSlaHours(remainingHours: number): string {
  if (remainingHours <= 0) return 'scaduto';
  return `${Math.round(remainingHours)}h`;
}
